import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const categoryLinks = [
  { label: "电影", to: "/category/电影" },
  { label: "电视剧", to: "/category/电视剧" },
  { label: "短视频", to: "/category/短视频" },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `relative text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
    isActive ? "text-primary-400" : "text-foreground-700 hover:text-primary-400"
  }`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const catRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-gradient-to-b from-background-950/70 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-950 flex items-center justify-center">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-foreground-950">黄瓜视频</span>
            <span className="text-[10px] tracking-widest text-foreground-500 uppercase">Cucumber Video</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/" className={navLinkClass} end>
            首页
          </NavLink>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                catOpen || location.pathname.startsWith("/category")
                  ? "text-primary-400"
                  : "text-foreground-700 hover:text-primary-400"
              }`}
            >
              视频分类
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-40 py-2 rounded-lg bg-background-100 border border-background-200/70">
                {categoryLinks.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-200 hover:text-primary-400 transition-colors cursor-pointer"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/download" className={navLinkClass}>
            下载
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            关于我们
          </NavLink>
        </nav>

        <form onSubmit={submitSearch} className="hidden lg:flex items-center flex-1 max-w-sm ml-auto">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base w-5 h-5 flex items-center justify-center" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频标题或简介"
              className="w-full h-10 pl-10 pr-3 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
          </div>
        </form>

        <Link
          to="/download"
          className="hidden md:flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer lg:ml-0 ml-auto"
        >
          <i className="ri-download-2-line w-4 h-4 flex items-center justify-center" />
          下载App
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开菜单"
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 pb-5 pt-4">
          <form onSubmit={submitSearch} className="relative w-full mb-4">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 w-5 h-5 flex items-center justify-center" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频标题或简介"
              className="w-full h-11 pl-10 pr-3 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
            />
          </form>
          <nav className="flex flex-col">
            {[{ label: "首页", to: "/" }, ...categoryLinks, { label: "下载", to: "/download" }, { label: "关于我们", to: "/about" }].map(
              (item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="py-3 text-base text-foreground-800 border-b border-background-200/60 hover:text-primary-400 transition-colors cursor-pointer"
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      )}
    </header>
  );
}